var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * ContainerNode
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Menu } from 'antd';
import { RightOutlined, DownOutlined, CheckOutlined, CloseCircleOutlined, AppstoreAddOutlined, NumberOutlined, DropboxOutlined } from '@ant-design/icons';
import { ContainerStatus, ContainerStatusLabels } from '../types';
import { getContainerDisplayId } from '../shared/containerHelpers';
import { PackerBadge, usePackerForContainer } from '../shared';
export var ContainerNode = function (_a) {
    var container = _a.container, allContainers = _a.allContainers, containerTypeMap = _a.containerTypeMap, selectedContainerId = _a.selectedContainerId, _b = _a.depth, depth = _b === void 0 ? 0 : _b, onSelect = _a.onSelect, onContextAction = _a.onContextAction, onBulkPack = _a.onBulkPack;
    var t = useTranslation().t;
    var _c = useState(true), isExpanded = _c[0], setIsExpanded = _c[1];
    // Check if selected container is a descendant — expand if collapsed
    var hasSelectedDescendant = useMemo(function () {
        if (!selectedContainerId)
            return false;
        var check = function (c) {
            var _loop_1 = function (childId) {
                if (childId === selectedContainerId)
                    return { value: true };
                var child = allContainers.find(function (x) { return x.containerId === childId; });
                if (child && check(child))
                    return { value: true };
            };
            for (var _i = 0, _a = c.childContainerIdList; _i < _a.length; _i++) {
                var childId = _a[_i];
                var state_1 = _loop_1(childId);
                if (typeof state_1 === "object")
                    return state_1.value;
            }
            return false;
        };
        return check(container);
    }, [selectedContainerId, container, allContainers]);
    useEffect(function () {
        if (hasSelectedDescendant) {
            setIsExpanded(true);
        }
    }, [hasSelectedDescendant]);
    var isSelected = container.containerId === selectedContainerId;
    var isClosed = container.statusId === ContainerStatus.Closed;
    var hasChildren = container.childContainerIdList.length > 0;
    var statusLabel = ContainerStatusLabels[container.statusId] || container.statusId;
    var childContainers = useMemo(function () {
        return container.childContainerIdList
            .map(function (childId) {
            return allContainers.find(function (c) { return c.containerId === childId; });
        })
            .filter(function (c) { return c != null; });
    }, [container.childContainerIdList, allContainers]);
    var toggleExpand = useCallback(function (e) {
        e.stopPropagation();
        setIsExpanded(function (prev) { return !prev; });
    }, []);
    var handleSelect = useCallback(function () {
        onSelect(container.containerId);
    }, [onSelect, container.containerId]);
    // Determine display info
    var isRoot = container.isRoot;
    var typeLabel = container.containerTypeEnumId
        ? (containerTypeMap.get(container.containerTypeEnumId) || (isRoot ? 'Container' : 'Carton'))
        : (isRoot ? 'Container' : 'Carton');
    var displayLabel = typeLabel + " #" + getContainerDisplayId(container);
    // Look up packer for the container
    var _d = usePackerForContainer(container.containerId), packerName = _d.packerName, isMyContainer = _d.isMine;
    var childCount = container.childContainerIdList.length;
    // Sum own items + all descendant items
    var contentCount = useMemo(function () {
        var sumItems = function (c) {
            var total = c.totalQuantity || 0;
            var _loop_2 = function (childId) {
                var child = allContainers.find(function (x) { return x.containerId === childId; });
                if (child)
                    total += sumItems(child);
            };
            for (var _i = 0, _a = c.childContainerIdList; _i < _a.length; _i++) {
                var childId = _a[_i];
                _loop_2(childId);
            }
            return total;
        };
        return sumItems(container);
    }, [container, allContainers]);
    // Tree line positioning for child hierarchy guides
    var treeLineLeft = (depth * 24) - 12;
    var treeConnectorWidth = 10;
    return (_jsxs(_Fragment, { children: [_jsx(Dropdown, __assign({ overlay: _jsxs(Menu, __assign({ className: 'font-inter', style: {
                        fontSize: 12,
                        minWidth: 160,
                        borderRadius: 8,
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
                        padding: '4px 0'
                    } }, { children: [_jsx(Menu.Item, __assign({ icon: isSelected ? _jsx(CloseCircleOutlined, {}, void 0) : _jsx(CheckOutlined, {}, void 0), style: { fontSize: 12 }, onClick: function () { return onSelect(container.containerId); } }, { children: isSelected ? t('Deselect') : t('Select') }), 'select'), isRoot && !isClosed && onBulkPack && (_jsx(Menu.Item, __assign({ icon: _jsx(AppstoreAddOutlined, {}, void 0), style: { fontSize: 12 }, onClick: function () { return onBulkPack(container.containerId); } }, { children: t('Bulk Pack') }), 'bulk-pack'))] }), void 0), trigger: ['contextMenu'] }, { children: _jsxs("div", __assign({ className: "\n          flex items-center gap-2 cursor-pointer select-none\n          rounded-md transition-colors\n          " + (isSelected ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-800 hover:bg-gray-100') + "\n        ", style: {
                        padding: '6px 8px',
                        marginTop: isRoot ? 4 : 0,
                        marginLeft: (depth - 1) * 24,
                        border: '1px solid #e5e7eb',
                        borderRadius: isRoot ? 6 : '0 0 6px 6px',
                        borderTop: isRoot ? '1px solid #e5e7eb' : 'none',
                    }, onClick: handleSelect }, { children: [hasChildren ? (_jsx("span", __assign({ className: "flex-shrink-0 w-4 text-center cursor-pointer " + (isSelected ? 'text-white' : 'text-gray-400'), style: { marginTop: 2 }, onClick: toggleExpand }, { children: isExpanded ? (_jsx(DownOutlined, { style: { fontSize: 9 } }, void 0)) : (_jsx(RightOutlined, { style: { fontSize: 9 } }, void 0)) }), void 0)) : (_jsx("span", { className: 'flex-shrink-0 w-4' }, void 0)), _jsxs("div", __assign({ className: 'flex-1 min-w-0' }, { children: [_jsxs("div", __assign({ className: 'flex items-center gap-1.5 justify-between' }, { children: [_jsx("span", __assign({ className: 'font-semibold truncate', style: { fontSize: 13 } }, { children: displayLabel }), void 0), _jsx("span", __assign({ className: "rounded px-1.5 py-0.5 font-semibold flex-shrink-0 whitespace-nowrap " + (isSelected
                                                ? (isClosed ? 'bg-blue-400 text-blue-100' : 'bg-blue-500 text-white')
                                                : (isClosed ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600')), style: { fontSize: 10, lineHeight: '16px' } }, { children: t(statusLabel) }), void 0)] }), void 0), _jsxs("div", __assign({ className: 'flex items-center gap-2 mt-0.5' }, { children: [_jsxs("span", __assign({ className: "flex items-center gap-0.5 " + (isSelected ? 'text-blue-200' : 'text-gray-400'), style: { fontSize: 11 } }, { children: [_jsx(NumberOutlined, { style: { fontSize: 10, display: 'flex' } }, void 0), contentCount] }), void 0), isRoot && childCount > 0 && (_jsxs("span", __assign({ className: "flex items-center gap-0.5 " + (isSelected ? 'text-blue-200' : 'text-gray-400'), style: { fontSize: 11 } }, { children: [_jsx(DropboxOutlined, { style: { fontSize: 10, display: 'flex' } }, void 0), childCount] }), void 0)), packerName && (_jsx("div", __assign({ className: 'ml-auto min-w-0' }, { children: _jsx(PackerBadge, { packerName: packerName, isMine: isMyContainer, isSelected: isSelected }, void 0) }), void 0))] }), void 0)] }), void 0)] }), void 0) }), void 0), isExpanded && hasChildren && (_jsx(_Fragment, { children: childContainers.map(function (child, index) {
                    var isLastChild = index === childContainers.length - 1;
                    return (_jsxs("div", __assign({ className: 'relative' }, { children: [isLastChild ? (_jsx("div", { className: 'pointer-events-none', style: {
                                    position: 'absolute',
                                    left: treeLineLeft,
                                    top: 0,
                                    height: 16,
                                    width: treeConnectorWidth,
                                    borderLeft: '1.5px solid #9ca3af',
                                    borderBottom: '1.5px solid #9ca3af',
                                    borderBottomLeftRadius: 6,
                                } }, void 0)) : (_jsxs(_Fragment, { children: [_jsx("div", { className: 'pointer-events-none', style: {
                                            position: 'absolute',
                                            left: treeLineLeft,
                                            top: 0,
                                            bottom: 0,
                                            borderLeft: '1.5px solid #9ca3af',
                                        } }, void 0), _jsx("div", { className: 'pointer-events-none', style: {
                                            position: 'absolute',
                                            left: treeLineLeft,
                                            top: 16,
                                            width: treeConnectorWidth,
                                            borderTop: '1.5px solid #9ca3af',
                                        } }, void 0)] }, void 0)), _jsx(ContainerNode, { container: child, allContainers: allContainers, containerTypeMap: containerTypeMap, selectedContainerId: selectedContainerId, depth: depth + 1, onSelect: onSelect, onContextAction: onContextAction, onBulkPack: onBulkPack }, void 0)] }), child.containerId));
                }) }, void 0))] }, void 0));
};
export default ContainerNode;
